import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import Icon from "./Icon.js";
export function Table(props) {
    const { columns, rows, selectedId, onRowClick, emptyState, footer, stickyHeader, initialSort, caption, } = props;
    const [sort, setSort] = useState(initialSort ?? null);
    // Auto-sticky when row count > 20 (matches kit primitives.jsx:369).
    const sticky = stickyHeader || rows.length > 20;
    const sortedRows = useMemo(() => {
        if (!sort)
            return rows;
        const col = columns.find((c) => c.key === sort.key);
        if (!col || !col.sortable)
            return rows;
        const valueOf = col.sortValue ?? ((r) => r[sort.key]);
        const dirSign = sort.dir === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const va = valueOf(a);
            const vb = valueOf(b);
            if (va == null && vb == null)
                return 0;
            if (va == null)
                return 1; // nulls last (matches kit)
            if (vb == null)
                return -1;
            if (typeof va === "number" && typeof vb === "number") {
                return (va - vb) * dirSign;
            }
            return String(va).localeCompare(String(vb)) * dirSign;
        });
    }, [rows, sort, columns]);
    const onHeaderClick = (col) => {
        if (!col.sortable)
            return;
        setSort((prev) => {
            if (!prev || prev.key !== col.key)
                return { key: col.key, dir: "asc" };
            if (prev.dir === "asc")
                return { key: col.key, dir: "desc" };
            return null; // third click clears sort (matches kit)
        });
    };
    const onRowKey = (row) => (e) => {
        if (!onRowClick)
            return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRowClick(row);
        }
    };
    const isEmpty = sortedRows.length === 0;
    const wrapCls = ["tbl-wrap", sticky ? "tbl-wrap--sticky" : ""]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { className: wrapCls, children: [_jsxs("table", { className: "dtbl", role: "table", children: [caption ? (_jsx("caption", { className: "tbl-caption", children: caption })) : null, _jsx("thead", { children: _jsx("tr", { role: "row", children: columns.map((col) => {
                                const isSorted = sort != null && sort.key === col.key;
                                const ariaSortAttr = isSorted
                                    ? sort.dir === "asc"
                                        ? "ascending"
                                        : "descending"
                                    : col.sortable
                                        ? "none"
                                        : undefined;
                                const thCls = [
                                    col.sortable ? "is-sortable" : "",
                                    col.align === "right" ? "num" : "",
                                    col.className ?? "",
                                ]
                                    .filter(Boolean)
                                    .join(" ") || undefined;
                                return (_jsx("th", { role: "columnheader", "aria-sort": ariaSortAttr, className: thCls, style: col.width != null ? { width: col.width } : undefined, onClick: col.sortable ? () => onHeaderClick(col) : undefined, onKeyDown: col.sortable
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                onHeaderClick(col);
                                            }
                                        }
                                        : undefined, tabIndex: col.sortable ? 0 : undefined, children: _jsxs("span", { className: "th-inner", children: [_jsx("span", { children: col.label }), col.sortable ? (_jsx("span", { className: `th-caret${isSorted ? ` is-${sort.dir}` : ""}`, "aria-hidden": "true", children: _jsx(Icon, { name: isSorted && sort.dir === "desc"
                                                        ? "chevron-down"
                                                        : "chevron-up", size: 11 }) })) : null] }) }, col.key));
                            }) }) }), !isEmpty && (_jsx("tbody", { children: sortedRows.map((row) => {
                            const trCls = [
                                onRowClick ? "is-clickable" : "",
                                selectedId === row.id ? "is-selected" : "",
                            ]
                                .filter(Boolean)
                                .join(" ") || undefined;
                            return (_jsx("tr", { role: "row", className: trCls, tabIndex: onRowClick ? 0 : undefined, onClick: onRowClick ? () => onRowClick(row) : undefined, onKeyDown: onRowClick ? onRowKey(row) : undefined, children: columns.map((col) => {
                                    const tdCls = [
                                        col.align === "right" ? "num" : "",
                                        col.cellClassName ?? "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || undefined;
                                    const cell = col.render
                                        ? col.render(row)
                                        : row[col.key];
                                    return (_jsx("td", { role: "cell", className: tdCls, children: cell }, col.key));
                                }) }, row.id));
                        }) }))] }), isEmpty && (_jsx("div", { className: "tbl-empty", children: emptyState ?? (_jsx("span", { className: "tbl-empty-fallback", children: "No rows." })) })), footer != null ? (_jsx("div", { className: "tbl-foot", children: footer })) : null] }));
}
export default Table;
//# sourceMappingURL=Table.js.map